/**
 * Sourcing summary — the Style File's answer to "can cutting start?", and the
 * place the operator answers it.
 *
 * The Overview previously said nothing about sourcing beyond a rail badge that
 * rendered "3/4 sourced" in the same muted grey whether a line was short or
 * simply not started — so a blocked style looked identical to a fine one.
 *
 * Unlike its read-only Costing and Sampling siblings, this card *acts*: issuing
 * materials is the gate on production, and the product feedback was that
 * burying it inside Sourcing hid the one step without which nothing starts. So
 * the card states the consequence in a sentence ("Cutting hasn't started"),
 * and issues every ready material in one click rather than making a
 * non-technical user open a dialog per material and choose a source in each.
 *
 * The confirm step is the point: issuing is append-only — a correction is a new
 * entry, never an edit — so the risk in one-click bulk isn't the bulk, it's
 * doing it blind. The dialog names every material and quantity first.
 *
 * Partial issues (800 kg now, the rest on Friday) stay on the Sourcing screen.
 * That's the exception, and making every user pay for it here is what made the
 * old screen unreadable.
 *
 * @see apps/web/src/features/styles/components/materials-section.tsx — the working screen
 * @see packages/domain/src/garment/materials/issue-ready-to-cutting.ts
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, PackageCheck, Scissors, ShoppingCart } from "lucide-react";
import {
  Button,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  toast,
} from "@flint/ui";
import type { materials as materialsContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import { milliToQty } from "../../../lib/format.js";
import { SectionCard } from "./style-cockpit-chrome.js";
import { PpLockNote } from "./pp-gate-card.js";

type MaterialsData = materialsContract.MaterialsViewWire;
type Line = MaterialsData["lines"][number];
type Plan = Line["plan"];

/**
 * Materials grouped by what the operator can *do*, not by what they are.
 *
 * The flat list this replaces gave four identically-weighted rows and asked the
 * reader to decode a status pill on each to work out which one needed them —
 * with the action floating at the bottom, detached from the material it applied
 * to. Grouping answers "what can I do right now?" before it answers "what is
 * the state of each line?", and lets the button sit inside the group it acts on.
 *
 * Order is deliberate: what's actionable first, what's merely waiting next,
 * what's already done last.
 */
const GROUPS = [
  {
    key: "ready",
    title: "Ready to issue",
    hint: "In store now — send these to cutting.",
    tone: "ok",
    plans: ["ready"],
  },
  {
    key: "blocked",
    title: "Still to arrange",
    hint: "Cutting can't start on these yet.",
    tone: "warn",
    plans: ["to_source", "partly_ready"],
  },
  {
    key: "awaiting",
    title: "On the way",
    hint: "Ordered — waiting on the vendor.",
    tone: "sky",
    plans: ["awaiting"],
  },
  {
    key: "issued",
    title: "With cutting",
    hint: "Already issued to the floor.",
    tone: "muted",
    plans: ["issued"],
  },
] as const satisfies ReadonlyArray<{
  key: string;
  title: string;
  hint: string;
  tone: "ok" | "warn" | "sky" | "muted";
  plans: ReadonlyArray<Plan>;
}>;

const DOT_TONE: Record<"ok" | "warn" | "sky" | "muted", string> = {
  ok: "bg-ok",
  warn: "bg-warn",
  sky: "bg-sky-500",
  muted: "bg-muted/50",
};

/**
 * The gate sentence. Deliberately phrased as what *hasn't* happened — a status
 * pill reading "Not issued" doesn't tell a new operator that the floor is
 * waiting on them.
 */
function gate(
  lines: Line[],
  readyCount: number,
  ppApproved: boolean,
): { text: string; tone: "ok" | "warn" | "muted" } {
  const issued = lines.filter((l) => l.plan === "issued").length;

  if (lines.length === 0) return { text: "No materials on this style yet.", tone: "muted" };
  // Before the PP gate this card is read at a different moment in the style's
  // life: the sample is cut from real material, so what matters here is whether
  // fabric and trims are in hand at all — not bulk issuing, which is still two
  // steps away. Leading with "waiting on PP approval" here answered a question
  // the reader hasn't reached yet.
  if (!ppApproved) {
    const toOrder = lines.filter(
      (l) => l.plan === "to_source" || l.plan === "partly_ready",
    ).length;
    if (toOrder > 0) {
      return {
        text:
          toOrder === 1
            ? "1 material still to order — sampling needs it in hand."
            : `${toOrder} materials still to order — sampling needs them in hand.`,
        tone: "warn",
      };
    }
    const waiting = lines.filter((l) => l.plan === "awaiting").length;
    if (waiting > 0) {
      return {
        text:
          waiting === 1
            ? "Waiting on 1 delivery before sampling can start."
            : `Waiting on ${waiting} deliveries before sampling can start.`,
        tone: "muted",
      };
    }
    return {
      text: "Materials are in. Issuing to cutting unlocks once PP is approved.",
      tone: "ok",
    };
  }
  if (issued === lines.length) {
    return { text: "All materials are with cutting. Production can start.", tone: "ok" };
  }
  if (issued === 0 && readyCount === 0) {
    return {
      text: "Cutting hasn't started — no material is ready to issue yet.",
      tone: "muted",
    };
  }
  if (issued === 0) {
    return { text: "Cutting hasn't started. Issue materials to begin production.", tone: "warn" };
  }
  return {
    text: `${issued} of ${lines.length} materials issued — cutting has started on those.`,
    tone: "warn",
  };
}

export function SourcingSummaryCard({
  styleId,
  materials,
  loading,
  ppApproved,
}: {
  styleId: string;
  materials: MaterialsData | undefined;
  loading: boolean;
  /** False until the PP gate is signed off — issuing is locked, ordering isn't. */
  ppApproved: boolean;
}) {
  const utils = trpc.useUtils();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const lines = (materials?.hasBom ? materials.lines : []) as Line[];
  const ready = lines.filter((l) => l.plan === "ready");
  const issuedCount = lines.filter((l) => l.plan === "issued").length;
  const g = gate(lines, ready.length, ppApproved);

  const issueReady = trpc.materials.issueReadyToCutting.useMutation({
    onSuccess: async (res: { issued: { label: string }[] }) => {
      setConfirmOpen(false);
      await Promise.all([
        utils.materials.getForStyle.invalidate({ styleId }),
        utils.stock.summary.invalidate(),
        utils.auditLog.list.invalidate(),
      ]);
      const names = res.issued.map((i) => i.label);
      toast.success(
        names.length === 1 ? `${names[0]} issued to cutting` : `${names.length} materials issued to cutting`,
        { description: names.length > 1 ? names.join(" · ") : undefined },
      );
    },
    onError: (e: { message: string }) => toast.error("Couldn't issue materials", { description: e.message }),
  });

  // No status pill here on purpose: the gate strip immediately below already
  // says what's outstanding, and two summaries competing in one card was the
  // exact "which do I read?" problem this redesign set out to remove.
  const action = (
    <>
      <Link
        to="/merchandising/styles/$styleId/materials"
        params={{ styleId }}
        className="flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-800"
      >
        Go to Sourcing <ArrowRight size={12} />
      </Link>
    </>
  );

  return (
    <SectionCard
      title="Sourcing"
      meta={lines.length > 0 ? `${lines.length} materials on this style` : "Materials for this style"}
      id="sec-sourcing-summary"
      action={action}
    >
      {loading ? (
        <p className="text-sm text-muted">Loading sourcing…</p>
      ) : lines.length === 0 ? (
        <div className="rounded-sm border border-dashed border-line bg-bg px-4 py-5">
          <p className="text-sm text-muted">
            No materials yet — lock the costing to generate the bill of materials.
          </p>
        </div>
      ) : (
        <>
          {/* ── The rule, stated once, above everything it explains. ── */}
          <div
            className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-sm border px-3.5 py-2.5 ${
              g.tone === "warn"
                ? "border-warn/25 bg-warn/5"
                : g.tone === "ok"
                  ? "border-ok/25 bg-ok/5"
                  : "border-line bg-bg"
            }`}
          >
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT_TONE[g.tone]}`} />
            <p className={`text-sm font-medium ${g.tone === "muted" ? "text-muted" : "text-ink"}`}>
              {g.text}
            </p>
            <span className="ml-auto shrink-0 font-mono text-xs text-muted">
              {issuedCount}/{lines.length} issued
            </span>
          </div>

          {/* ── One row per group: dot, name, count, its next step.
               Per-material detail deliberately stays on the Sourcing screen —
               with 8-10 materials the old expanded list dominated the Style
               File, and the counts plus the buttons are what gets acted on.
               Height is now fixed: 4 materials and 40 render identically. ── */}
          <ul className="mt-3 flex flex-col divide-y divide-line rounded-sm border border-line">
            {GROUPS.map((group) => {
              const rows = lines.filter((l) => (group.plans as ReadonlyArray<Plan>).includes(l.plan));
              if (rows.length === 0) return null;

              return (
                <li
                  key={group.key}
                  className="flex flex-wrap items-center gap-x-2.5 gap-y-2 px-3.5 py-2.5"
                >
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT_TONE[group.tone]}`} />
                  <span className="text-sm font-medium text-ink">{group.title}</span>
                  <span className="font-mono text-xs text-muted">{rows.length}</span>
                  <span className="hidden text-xs text-muted sm:inline">{group.hint}</span>

                  {/* Only issuing happens here — ordering needs a vendor and a
                      rate, recording arrival needs the 4-Point inspection, so
                      those hand off to the screen that asks for them. */}
                  {group.key === "ready" ? (
                    <span className="ml-auto shrink-0">
                      {ppApproved ? (
                        <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={issueReady.isPending}>
                          <Scissors size={13} />
                          Issue {rows.length === 1 ? "it" : `all ${rows.length}`} to cutting
                        </Button>
                      ) : (
                        <PpLockNote />
                      )}
                    </span>
                  ) : group.key === "blocked" ? (
                    <span className="ml-auto shrink-0">
                      <Button size="sm" intent="ghost" asChild>
                        <Link to="/merchandising/styles/$styleId/materials" params={{ styleId }}>
                          <ShoppingCart size={13} />
                          Order {rows.length === 1 ? "it" : "these"}
                        </Link>
                      </Button>
                    </span>
                  ) : group.key === "awaiting" ? (
                    <span className="ml-auto shrink-0">
                      <Button size="sm" intent="ghost" asChild>
                        <Link to="/production/floor/inward">
                          <PackageCheck size={13} />
                          Record arrival
                        </Link>
                      </Button>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>

        </>
      )}

      <ConfirmIssueDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        ready={ready}
        busy={issueReady.isPending}
        onConfirm={() => issueReady.mutate({ styleId })}
      />
    </SectionCard>
  );
}

/**
 * Names every material and quantity before committing. Issuing is append-only,
 * so this is the last point at which the operator can still change their mind.
 */
function ConfirmIssueDialog({
  open, onOpenChange, ready, busy, onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ready: Line[];
  busy: boolean;
  onConfirm: () => void;
}) {
  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Issue to cutting?</DialogTitle>
          <DialogDescription>
            These materials leave the store and go to the floor. This is recorded as it happens and
            can't be edited later — a correction is a new entry.
          </DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col gap-1.5 rounded-sm border border-line bg-bg px-3.5 py-3">
          {ready.map((l) => (
            <li key={l.bomLineId} className="flex items-center justify-between gap-3">
              <span className="truncate text-sm text-ink">{l.label}</span>
              <span className="shrink-0 font-mono text-xs text-muted">
                {milliToQty(l.stillNeededQtyMilli, l.uom)}
              </span>
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button intent="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={busy}>
            {busy ? "Issuing…" : "Issue to cutting"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
